import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { downloadBlob, filenameFromContentDisposition } from '../utils/downloadBlob';
import { gerarWorkbookPecas } from '../utils/exportarPecasExcel';
import { trocarEixosComprimentoLargura } from '../utils/trocarEixosStepPeca';
import './StepConverter.css';

// fetch() não faz parse automático de erro como o interceptor do axios
// (services/api.js) — sem isso, o `detail` real do backend era descartado
// e o usuário só via uma mensagem genérica fixa, mesmo quando o backend
// mandava algo específico (ex.: "Arquivo deve ser .step ou .stp").
async function extrairMensagemErro(response, fallback) {
  try {
    const data = await response.json();
    return data?.detail || fallback;
  } catch {
    return fallback;
  }
}

function StepConverter() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Índices de peça com comprimento/largura trocados pelo usuário (ciclo 11)
  // -- só um flag de exibição; a troca em si já foi aplicada em result.pecas.
  const [eixosTrocados, setEixosTrocados] = useState({});

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setResult(null);
      setError(null);
      setEixosTrocados({});
    }
  }, []);

  // A heurística do parser (backend/app/parser/step_parser_occ.py) assume
  // que o maior lado da peça é o "comprimento" -- mas essa é uma decisão de
  // MANUFATURA, não uma propriedade da geometria: o STEP não carrega eixo
  // nomeado nenhum (confirmado, ciclo 11), e um caso real (510524003/DIVISAO)
  // já mostrou a heurística errada sem nenhum sinal visível nos números
  // (comprimento=303/largura=269, parece uma peça normal). Por isso a
  // confirmação abaixo aparece sempre, não só quando a peça "parece" quase
  // quadrada -- ver docs/referencia/parser-step.md §3.
  const trocarEixosDaPeca = (pecaIndex) => {
    setResult((r) => ({
      ...r,
      pecas: r.pecas.map((p, i) => (i === pecaIndex ? trocarEixosComprimentoLargura(p) : p)),
    }));
    setEixosTrocados((t) => ({ ...t, [pecaIndex]: !t[pecaIndex] }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.step', '.STEP', '.stp', '.STP']
    },
    multiple: false
  });

  const convertToMPR = async () => {
    // Gera a partir de result.pecas (já parseado, possivelmente com eixos
    // trocados pelo usuário) -- NUNCA reabre o STEP original. O endpoint
    // /to-mpr antigo reparseava o arquivo do zero e por isso ignorava
    // qualquer troca de eixos feita aqui na tela (achado do ciclo 11).
    if (!result || !result.pecas || !result.pecas.length) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/step/mpr-de-dados', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pecas: result.pecas }),
      });

      if (!response.ok) {
        throw new Error(await extrairMensagemErro(response, 'Erro ao converter arquivo'));
      }

      // Usar filename do Content-Disposition (.mpr para 1 peça, .zip para várias)
      const filename = filenameFromContentDisposition(response.headers.get('content-disposition'))
        || selectedFile.name.replace(/\.(step|stp)$/i, '.mpr');

      const blob = await response.blob();
      downloadBlob(blob, filename);

      setResult({ success: true, message: 'Arquivo convertido com sucesso!' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const parseStep = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/step/parse', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(await extrairMensagemErro(response, 'Erro ao processar arquivo'));
      }

      const data = await response.json();
      setResult(data);
      setEixosTrocados({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const baixarExcel = async () => {
    if (!result || !result.pecas) return;

    const workbook = await gerarWorkbookPecas(result.pecas);

    const nomeArquivo = selectedFile
      ? selectedFile.name.replace(/\.(step|stp)$/i, '.xlsx')
      : 'dados_pecas.xlsx';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, nomeArquivo);
  };

  return (
    <>
      <h1 className="cw-page-title">Conversor STEP</h1>
      <p className="cw-page-subtitle">Arquivos STEP de qualquer software salvo em .step</p>
      <div className="step-converter">
      <div
        {...getRootProps()} 
        className={`dropzone ${isDragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
      >
        <input {...getInputProps()} />
        {selectedFile ? (
          <div className="file-info">
            <span className="file-icon"></span>
            <div className="file-details">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">{(selectedFile.size / 1024).toFixed(2)} KB</p>
            </div>
            <button 
              className="remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                setResult(null);
                setError(null);
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="dropzone-content">
            <span className="upload-icon"></span>
            <p className="upload-text">
              {isDragActive 
                ? 'Solte o arquivo aqui...' 
                : 'Selecione um arquivo .step ou clique para selecionar'}
            </p>
          </div>
        )}
      </div>

      {selectedFile && (
        <div className="actions">
          <button
            className="btn btn-secondary"
            onClick={parseStep}
            disabled={loading}
          >
            {loading ? 'Processando...' : 'Converter Arquivo'}
          </button>
          {result && result.pecas && (
            <>
              <button
                className="btn btn-primary"
                onClick={convertToMPR}
                disabled={loading}
              >
                {loading ? 'Convertendo...' : 'Baixar MPR'}
              </button>
              <button
                className="btn btn-success"
                onClick={baixarExcel}
                disabled={loading}
              >
                Baixar Excel
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {result && result.success && (
        <div className="success-message">
          {result.message}
        </div>
      )}

      {result && result.pecas && (
        <div className="result-panel">
          <h3>Dados Extraídos</h3>

          {result.resumo && (
            <div className="result-grid">
              <div className="result-item">
                <span className="label">Peças:</span>
                <span className="value">{result.resumo.total_pecas}</span>
              </div>
              <div className="result-item">
                <span className="label">Furos:</span>
                <span className="value">{result.resumo.total_furos}</span>
              </div>
              <div className="result-item">
                <span className="label">Rasgos:</span>
                <span className="value">{result.resumo.total_rasgos}</span>
              </div>
            </div>
          )}

          {result.pecas.map((peca, pecaIndex) => (
            <div key={pecaIndex} className="peca-section">
              <h4 className="peca-title">
                {peca.nome} — {peca.comprimento} x {peca.largura} x {peca.espessura} mm
              </h4>

              <div className="orientacao-aviso">
                <span>
                  ⚠️ Confira a orientação: o parser assume o maior lado como
                  "comprimento" ({peca.comprimento} mm), mas isso é uma decisão
                  de manufatura, não da geometria — pode estar invertido em
                  relação ao cadastro.
                  {eixosTrocados[pecaIndex] && ' (eixos já trocados nesta tela)'}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => trocarEixosDaPeca(pecaIndex)}
                >
                  Trocar comprimento ↔ largura
                </button>
              </div>

              {peca.furos && peca.furos.length > 0 && (
                <div className="furos-table">
                  <h5>Furações ({peca.furos.length})</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>X</th>
                        <th>Y</th>
                        <th>Ø</th>
                        <th>Prof.</th>
                        <th>Tipo</th>
                        <th>Lado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {peca.furos.map((furo, index) => (
                        <tr key={index}>
                          <td>{furo.id}</td>
                          <td>{furo.x} mm</td>
                          <td>{furo.y} mm</td>
                          <td>{furo.diametro} mm</td>
                          <td>{furo.profundidade} mm</td>
                          <td>{furo.tipo}</td>
                          <td>{furo.lado}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {peca.rasgos && peca.rasgos.length > 0 && (
                <div className="furos-table">
                  <h5>Rasgos ({peca.rasgos.length})</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>X</th>
                        <th>Y</th>
                        <th>Larg.</th>
                        <th>Comp.</th>
                        <th>Prof.</th>
                        <th>Face</th>
                        <th>Direção</th>
                      </tr>
                    </thead>
                    <tbody>
                      {peca.rasgos.map((rasgo, index) => (
                        <tr key={index}>
                          <td>{rasgo.id}</td>
                          <td>{rasgo.x} mm</td>
                          <td>{rasgo.y} mm</td>
                          <td>{rasgo.largura} mm</td>
                          <td>{rasgo.comprimento} mm</td>
                          <td>{rasgo.profundidade} mm</td>
                          <td>{rasgo.face}</td>
                          <td>{rasgo.direcao}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(!peca.furos || peca.furos.length === 0) && (!peca.rasgos || peca.rasgos.length === 0) && (
                <p className="empty-hint">Nenhum furo ou rasgo detectado.</p>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </>
  );
}

export default StepConverter;
